import React, { FC, useContext, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { ChevronArrowDown } from '@td-client-frontend/icons';
import { PageSpinner } from '@td-client-frontend/common';
import { Profile } from './MarketingPreferences/MarketingPreferencesForm';
import { useGetMarketingProfileById, useMarketingProfileCache } from '../../hooks/useMarketingProfile';
import { MarketingProfileById } from '../../@types/Marketing';
import { alwaysSet } from '../../utils/marketingUtils';
import { PendingChangesContext } from '../../context/PendingChangesContext';
import { SaveModal } from './SaveModal';
import { Button } from '../Button';
import { useAuth } from '../../context/AuthContext';
import { PermMode } from '../WithPermission';
import { Permissions } from '../../@types/Permissions';

export const MARKETING_PROFILES_LIST_LINK = '/marketing-profiles';

export const MarketingProfileDetails: FC = () => {
  const { checkPerm } = useAuth();
  const { id: profileId } = useParams<{ id: string }>();
  const [profileData, setProfileData] = useState<MarketingProfileById>();
  const [isProfileValid, setIsProfileValid] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);

  const { isChanged, setIsChanged } = useContext(PendingChangesContext);
  const { invalidateProfileCache } = useMarketingProfileCache();
  const history = useHistory();

  useGetMarketingProfileById({ id: profileId || '', onSuccess: setProfileData });

  const handleProfileDataChange = (data: MarketingProfileById, isChanged = true) => {
    setIsChanged(isChanged);
    setProfileData(data);
  };

  const toggleSaveModal = () => setShowSaveModal((prev: boolean) => !prev);

  const onCancelChanges = async () => {
    await invalidateProfileCache(profileId);
    setIsChanged(false);
  };

  const navigateToProfilesListPage = () => history.push(MARKETING_PROFILES_LIST_LINK);

  const isSaveButtonDisabled: boolean = useMemo(() => {
    const hasOneWritePerm = checkPerm(
      [
        Permissions.MarketingProfilePreferencesWrite,
        Permissions.MarketingProfileRegistrationWrite,
        Permissions.MarketingProfileRetentionWrite
      ],
      PermMode.ANY
    );
    return (
      (profileData?.default
        ? !(checkPerm([Permissions.MarketingProfileBaseWrite]) && hasOneWritePerm)
        : !hasOneWritePerm) ||
      !isProfileValid ||
      !isChanged
    );
  }, [checkPerm, isChanged, isProfileValid, profileData]);

  return profileData ? (
    <div className="flex flex-col px-8 pb-5 max-h-min" data-testid="profileDetailsContent">
      <h1 className="text-4xl my-6 leading-10 font-bold">Marketing profile</h1>
      <Profile
        profileData={profileData}
        onValid={alwaysSet(setIsProfileValid, true)}
        onInvalid={alwaysSet(setIsProfileValid, false)}
        onChange={handleProfileDataChange}
        onDelete={navigateToProfilesListPage}
      />
      <div className="px-8 pt-6 border-t-2 rounded-b-lg flex justify-between gap-2 ">
        <Button
          onClick={navigateToProfilesListPage}
          kind="secondary"
          className="flex items-center px-6 text-lg h-12"
          data-testid="navigateBackButton"
        >
          <ChevronArrowDown color="#5B2F91" className="transform rotate-90 mr-2" />
          Back
        </Button>
        <div className="flex justify-between gap-3">
          <Button onClick={onCancelChanges} disabled={!isChanged} kind="secondary" data-testid="cancelButton">
            Cancel
          </Button>
          <Button onClick={toggleSaveModal} disabled={isSaveButtonDisabled} data-testid="saveButton">
            Save
          </Button>
        </div>
      </div>
      {showSaveModal && (
        <SaveModal isOpen={showSaveModal} toggleSaveModal={toggleSaveModal} profileData={profileData} />
      )}
    </div>
  ) : (
    <PageSpinner />
  );
};
